/**
 * useMyDocuments — 客户资料列表 + 上传 + 下载 hook（Issue #198）。
 *
 * 与 client-app 同名 hook 一致：
 *   - 完整的 grant → PUT → finalize 编排在 executeUploadFlow 中。
 *   - 列表 / 授权 / finalize / download 全部走 OpenAPI typed client。
 *   - 任何状态变更都以 finalize / download 后端响应为准，不写本地假状态。
 *   - 401 时尝试一次 refresh，再失败才向上报告 session_expired。
 *
 * 红线：
 *   - 不持久化签名 URL；调用方使用完即丢弃。
 *   - 不解析 OSS 密钥；签名 URL 完全由后端组装。
 *   - 上传失败不会写入客户端状态，避免 awaiting_upload 被误标记为已完成。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createCustomerMeApiClient,
  refreshCustomerSession,
} from './customerSession';
import { classifyMeFetchError, meError, type MeFetchError } from './classify';
import {
  defaultPutToSignedUrl,
  executeUploadFlow,
  type CustomerDocumentMeDto,
  type FinalizeCustomerDocumentUploadDto,
  type RequestCustomerDocumentUploadGrantDto,
  type RequestUploadGrantResponseDto,
  type UploadFlowInput,
} from './executeUploadFlow';
import {
  defaultCreateObjectUrl,
  defaultFetchSignedDocument,
  defaultRevokeObjectUrl,
  defaultTriggerObjectUrlDownload,
  executeDownloadFlow,
  type DownloadFlowInput,
  type DownloadFlowResult,
  type SignedDownloadGrantDto,
} from './executeDownloadFlow';

export type { CustomerDocumentMeDto } from './executeUploadFlow';
export type { SignedDownloadGrantDto } from './executeDownloadFlow';

interface UseMyDocumentsResult {
  documents: CustomerDocumentMeDto[];
  loading: boolean;
  error: MeFetchError | null;
  refetch: () => void;
  requestUploadGrant: (
    input: RequestCustomerDocumentUploadGrantDto,
  ) => Promise<RequestUploadGrantResponseDto | MeFetchError>;
  finalizeUpload: (
    documentId: string,
    input: FinalizeCustomerDocumentUploadDto,
  ) => Promise<CustomerDocumentMeDto | MeFetchError>;
  uploadDocument: (
    input: UploadFlowInput,
  ) => Promise<CustomerDocumentMeDto | MeFetchError>;
  fetchDownloadGrant: (
    documentId: string,
  ) => Promise<SignedDownloadGrantDto | MeFetchError>;
  downloadDocument: (input: DownloadFlowInput) => Promise<DownloadFlowResult>;
}

export function useMyDocuments(options?: {
  publishedOnly?: boolean;
}): UseMyDocumentsResult {
  const [documents, setDocuments] = useState<CustomerDocumentMeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<MeFetchError | null>(null);
  const [tick, setTick] = useState(0);
  const publishedOnlyRef = useRef(options?.publishedOnly);
  publishedOnlyRef.current = options?.publishedOnly;

  const refetch = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      const query: Record<string, boolean> = {};
      if (publishedOnlyRef.current) query.publishedOnly = true;
      try {
        const apiClient = createCustomerMeApiClient();
        const result = await apiClient.GET('/api/v1/me/documents', {
          params: { query },
        });
        if (cancelled) return;
        setDocuments([...(result.data ?? [])]);
      } catch (err: unknown) {
        if (cancelled) return;
        const classified = classifyMeFetchError(err);
        if (classified.kind === 'session_expired') {
          const refreshed = await refreshCustomerSession();
          if (cancelled) return;
          if (refreshed) {
            try {
              const apiClient = createCustomerMeApiClient();
              const retry = await apiClient.GET('/api/v1/me/documents', {
                params: { query },
              });
              setDocuments([...(retry.data ?? [])]);
              setError(null);
              return;
            } catch {
              setError(classified);
              return;
            }
          }
        }
        setError(classified);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const requestUploadGrant = useCallback(
    async (
      input: RequestCustomerDocumentUploadGrantDto,
    ): Promise<RequestUploadGrantResponseDto | MeFetchError> => {
      try {
        const apiClient = createCustomerMeApiClient();
        const { data } = await apiClient.POST(
          '/api/v1/me/documents/upload-grant',
          { body: input },
        );
        if (!data) {
          return meError('unknown', '上传授权返回为空');
        }
        return data;
      } catch (err) {
        return classifyMeFetchError(err);
      }
    },
    [],
  );

  const finalizeUpload = useCallback(
    async (
      documentId: string,
      input: FinalizeCustomerDocumentUploadDto,
    ): Promise<CustomerDocumentMeDto | MeFetchError> => {
      try {
        const apiClient = createCustomerMeApiClient();
        const { data } = await apiClient.POST(
          '/api/v1/me/documents/{id}/finalize',
          { params: { path: { id: documentId } }, body: input },
        );
        if (!data) return meError('unknown', 'finalize 返回为空');
        return data;
      } catch (err) {
        return classifyMeFetchError(err);
      }
    },
    [],
  );

  const uploadDocument = useCallback(
    async (
      input: UploadFlowInput,
    ): Promise<CustomerDocumentMeDto | MeFetchError> => {
      return executeUploadFlow(
        {
          requestUploadGrant,
          finalizeUpload,
          putToSignedUrl: defaultPutToSignedUrl,
        },
        input,
      );
    },
    [requestUploadGrant, finalizeUpload],
  );

  const fetchDownloadGrant = useCallback(
    async (
      documentId: string,
    ): Promise<SignedDownloadGrantDto | MeFetchError> => {
      try {
        const apiClient = createCustomerMeApiClient();
        const { data } = await apiClient.GET(
          '/api/v1/me/documents/{id}/download',
          { params: { path: { id: documentId } } },
        );
        if (!data) return meError('unknown', '下载授权返回为空');
        return data;
      } catch (err) {
        return classifyMeFetchError(err);
      }
    },
    [],
  );

  const downloadDocument = useCallback(
    async (input: DownloadFlowInput): Promise<DownloadFlowResult> => {
      return executeDownloadFlow(
        {
          fetchDownloadGrant,
          fetchSignedDocument: defaultFetchSignedDocument,
          createObjectUrl: defaultCreateObjectUrl,
          revokeObjectUrl: defaultRevokeObjectUrl,
          triggerObjectUrlDownload: defaultTriggerObjectUrlDownload,
        },
        input,
      );
    },
    [fetchDownloadGrant],
  );

  return {
    documents,
    loading,
    error,
    refetch,
    requestUploadGrant,
    finalizeUpload,
    uploadDocument,
    fetchDownloadGrant,
    downloadDocument,
  };
}
